import type { SVGProps } from "react";

/**
 * Set de íconos deco-noir del OS (iteración UI 2026-08-13). SVG a mano:
 * nítidos a cualquier tamaño, theman con currentColor (temas por época B9)
 * y comparten line-weight — la misma regla de "una sola mano" que los retratos.
 * El atlas definitivo llega en F4; este set es la base.
 */

type P = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 24, children, ...rest }: P) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Crest de la Agencia: el ojo de Argos en rombo con rayos déco */
export function IconAgencia(p: P) {
  return (
    <Svg {...p}>
      <path d="M12 2 L21 12 L12 22 L3 12 Z" />
      <ellipse cx="12" cy="12" rx="4.4" ry="2.9" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <path d="M12 5.2 v1.8 M12 17 v1.8 M6.2 12 h-1.6 M19.4 12 h-1.6" strokeWidth="1.1" />
    </Svg>
  );
}

/** Briefing: carta con sello de lacre */
export function IconBriefing(p: P) {
  return (
    <Svg {...p}>
      <rect x="3" y="5" width="18" height="14" rx="1" />
      <path d="M3 6.5 L12 13 L21 6.5" />
      <path d="M16.8 13.6 l1.7 1.7 l-1.7 1.7 l-1.7 -1.7 Z" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Chirp: pájaro geométrico déco */
export function IconChirp(p: P) {
  return (
    <Svg {...p}>
      <path d="M20 6.5 c-1.2 -0.9 -2.6 -1 -3.8 -0.3 c-1 -1.4 -2.9 -1.8 -4.3 -0.8 c-1.3 0.9 -1.8 2.5 -1.3 4 L4 8 l3.4 3.2 c-0.6 3.4 1.2 6.4 4.6 7.3 c4.2 1.1 8 -1.9 8 -6.4 V8.6 L22 7 Z" />
      <circle cx="15.4" cy="8.6" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Mail: bandeja con correo entrante */
export function IconMail(p: P) {
  return (
    <Svg {...p}>
      <path d="M3 13 h5 l1.5 2.5 h5 L16 13 h5" />
      <path d="M3 13 v5 a1 1 0 0 0 1 1 h16 a1 1 0 0 0 1 -1 v-5" />
      <path d="M12 3 v6 M9.4 6.8 L12 9.4 L14.6 6.8" />
    </Svg>
  );
}

/** Archivo: cajonera de fichas */
export function IconArchivo(p: P) {
  return (
    <Svg {...p}>
      <rect x="4" y="3.5" width="16" height="17" rx="1" />
      <path d="M4 9 h16 M4 14.5 h16" />
      <path d="M10 6.2 h4 M10 11.8 h4 M10 17.2 h4" strokeWidth="1.2" />
    </Svg>
  );
}

/** Visor: lupa con detalle */
export function IconVisor(p: P) {
  return (
    <Svg {...p}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.3 15.3 L21 21" strokeWidth="2" />
      <path d="M7.5 10.5 a3 3 0 0 1 3 -3" strokeWidth="1.1" />
    </Svg>
  );
}

/** Corcho: chinche clavada */
export function IconCorcho(p: P) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8" r="4.2" />
      <circle cx="12" cy="8" r="1.4" fill="currentColor" stroke="none" />
      <path d="M12 12.2 V21" />
      <path d="M9 20.8 c1.8 -1.2 4.2 -1.2 6 0" strokeWidth="1.1" />
    </Svg>
  );
}

/** Acusación: sello de goma */
export function IconAcusacion(p: P) {
  return (
    <Svg {...p}>
      <path d="M9 10.5 a3 3 0 1 1 6 0 c0 1.8 -1.2 2.4 -1.2 3.5 h-3.6 c0 -1.1 -1.2 -1.7 -1.2 -3.5 Z" />
      <path d="M6 17.5 a2 2 0 0 1 2 -2 h8 a2 2 0 0 1 2 2 V19 H6 Z" />
      <path d="M4.5 21.5 h15" />
    </Svg>
  );
}

/** Ajustes: engranaje déco */
export function IconAjustes(p: P) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 4.2 v2.2 M12 17.6 v2.2 M4.2 12 h2.2 M17.6 12 h2.2 M6.5 6.5 l1.6 1.6 M15.9 15.9 l1.6 1.6 M17.5 6.5 l-1.6 1.6 M8.1 15.9 l-1.6 1.6" />
    </Svg>
  );
}

/** Messenger: globo de diálogo con puntos */
export function IconMessenger(p: P) {
  return (
    <Svg {...p}>
      <path d="M4.5 6.5 h15 v10 h-8.5 l-3.5 3 v-3 h-3 Z" />
      <circle cx="9" cy="11.5" r="0.7" fill="currentColor" />
      <circle cx="12" cy="11.5" r="0.7" fill="currentColor" />
      <circle cx="15" cy="11.5" r="0.7" fill="currentColor" />
    </Svg>
  );
}

/** BancoMar: columnas de banco */
export function IconBanco(p: P) {
  return (
    <Svg {...p}>
      <path d="M4 9.5 L12 4.5 L20 9.5 Z" />
      <path d="M6 9.5 v7 M10 9.5 v7 M14 9.5 v7 M18 9.5 v7" />
      <path d="M4.5 16.5 h15 M4 19 h16" />
    </Svg>
  );
}

/** Registro Público: sello con cinta */
export function IconRegistro(p: P) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="10" r="5.5" />
      <circle cx="12" cy="10" r="2.4" />
      <path d="M9.5 14.8 L8 20 l4 -2 l4 2 l-1.5 -5.2" />
    </Svg>
  );
}

/** Campana de avisos */
export function IconBell(p: P) {
  return (
    <Svg {...p}>
      <path d="M12 4 a5.5 5.5 0 0 1 5.5 5.5 c0 3.6 1.1 4.6 1.9 5.4 H4.6 c0.8 -0.8 1.9 -1.8 1.9 -5.4 A5.5 5.5 0 0 1 12 4 Z" />
      <path d="M10.2 18.2 a1.9 1.9 0 0 0 3.6 0" />
      <path d="M12 2.4 v1.4" strokeWidth="1.2" />
    </Svg>
  );
}

export const APP_ICONS: Record<string, (p: P) => ReturnType<typeof Svg>> = {
  briefing: IconBriefing,
  chirp: IconChirp,
  mail: IconMail,
  archivo: IconArchivo,
  visor: IconVisor,
  corcho: IconCorcho,
  messenger: IconMessenger,
  acusacion: IconAcusacion,
  ajustes: IconAjustes,
  "mod.banco": IconBanco,
  "mod.registro": IconRegistro,
};

export function AppIcon({ appId, ...p }: P & { appId: string }) {
  const C = APP_ICONS[appId] ?? IconAgencia;
  return <C {...p} />;
}
